import { useEffect } from 'react';
import axios from 'axios';
import { getToken, removeToken } from './tokenService';
import { memoizedRefreshToken } from './refreshTokenService';
import { TOKEN, REFRESH_TOKEN } from '../common/constants/localStorage';
import { routesPath } from '../common/constants/routesPath';
import globalRouter from '../routes/GlobalRouter';
import globalNotification from '../common/hooks/globalNotification';
import { notificationType } from '../common/constants/notificationType';
import { constants } from '../common/constants/constants';
import i18next from 'i18next';
import { endImpersonation, isImpersonationEnded, isImpersonationTab } from './impersonationSession';

// the only writes an impersonation tab may send: ending the session and switching the impersonated team
const IMPERSONATION_WRITES = ['/impersonation/current/stop', '/impersonation/current/team'];

const isReadRequest = config => ['get', 'head'].includes((config.method || 'get').toLowerCase());

// a request the impersonation tab refuses before it leaves the browser; the response interceptor lets it through
const blockedRequest = reason => Object.assign(new Error(reason), { isImpersonationBlock: true });

const notifyImpersonation = (key, type = notificationType.WARNING) =>
    globalNotification.open?.({
        type,
        message: i18next.t(`impersonation:toast.${key}.message`),
        description: i18next.t(`impersonation:toast.${key}.description`),
        placement: 'top',
    });

function authInterceptor(config) {
    const finalConfig = { ...config };

    // read-only impersonation: nothing after the session ended, and no writes besides stop and team switch
    if (isImpersonationEnded()) {
        throw blockedRequest('Impersonation ended');
    }
    if (isImpersonationTab()) {
        if (!isReadRequest(finalConfig) && !IMPERSONATION_WRITES.some(path => finalConfig.url?.endsWith(path))) {
            notifyImpersonation('readOnly');
            throw blockedRequest('Read-only impersonation');
        }
    }

    const token = getToken(TOKEN);

    if (!finalConfig.headers) {
        finalConfig.headers = {};
    }
    if (token) {
        finalConfig.headers.Authorization = `Bearer ${token}`;
    }

    return finalConfig;
}
// Creating axios instance for all fetch requests
const instance = axios.create({
    baseURL: `${constants.API_BASE_URL}`,
});

// Creating axios instance for refreshing token request only
const refreshTokenInstance = axios.create({
    baseURL: `${constants.API_BASE_URL}`,
});

instance.interceptors.request.use(authInterceptor);

instance.interceptors.response.use(
    res => {
        return res;
    },
    async err => {
        const originalConfig = err.config;
        const errStatus = err?.response?.status;
        const errStatusText = err?.response?.statusText;

        // nothing is refreshed or replayed once an impersonation tab has ended; it is reloading
        if (err?.isImpersonationBlock || isImpersonationEnded()) {
            return Promise.reject(err);
        }

        // the caller shows this error itself (e.g. the impersonation modal); a 401 still refreshes as usual
        if (originalConfig?.skipGlobalErrorHandler && errStatus !== 401) {
            return Promise.reject(err);
        }

        // the caller shows these statuses itself and leaves every other one to this handler (e.g. a 409
        // JOB_ALREADY_TAKEN on a status change, SC-QA-127), so one error never gets two toasts
        if (errStatus !== 401 && originalConfig?.handledStatuses?.includes(errStatus)) {
            return Promise.reject(err);
        }

        // an impersonation tab never refreshes: any 401 ends the session; its own errors are toasts, not pages
        if (isImpersonationTab()) {
            const code = err?.response?.data?.code;
            if (errStatus === 401) {
                endImpersonation('ended');
                return Promise.reject(err);
            }
            // sent just before a team switch, with the old team's token: the page reloads its data anyway
            if (errStatus === 403 && code === 'IMPERSONATION_TEAM_CHANGED') {
                return Promise.reject(err);
            }
            if (errStatus === 403 && code === 'IMPERSONATION_READ_ONLY') {
                notifyImpersonation('readOnly');
                return Promise.reject(err);
            }
            if (errStatus === 503 && code === 'IMPERSONATION_UNAVAILABLE') {
                notifyImpersonation('unavailable', notificationType.ERROR);
                return Promise.reject(err);
            }
        }

        // Access Token was expired
        if (errStatus !== 401) {
            switch (errStatus) {
                case 400:
                case 423: // TOTP lockout (TOTP_LOCKED), shown by the 2FA form that sent the code
                    // We will display a notification at the same level when we send a request to the server
                    // Here, we simply reject the promise to avoid double notifications on bad request errors
                    return Promise.reject(err);
                case 403:
                    navigateWithError(errStatus, 'Sorry, you are not authorized to access this page.');
                    break;
                case 404:
                    navigateWithError(errStatus, 'Sorry, the page you visited does not exist.');
                    break;
                case 429:
                    globalNotification.open({
                        type: notificationType.ERROR,
                        message: 'Too many requests',
                        description: 'Sorry, you have exceeded the limit of requests.',
                    });
                    break;
                default:
                    globalNotification.open({
                        type: notificationType.ERROR,
                        message: errStatus ? errStatus + ' ' + errStatusText : 'Error',
                        description: 'Sorry, something went wrong.',
                    });
                    return Promise.reject(err);
            }

            return Promise.reject(err);
        }

        // Nothing to refresh with (the 2FA code step has no refresh token yet): the caller handles the 401
        if (!getToken(REFRESH_TOKEN)) {
            return Promise.reject(err);
        }

        // A replayed request that 401s again is not refreshed again: the server rejects this token
        // (e.g. a session from before a token-format change), so retrying would loop forever.
        if (originalConfig._retry) {
            return Promise.reject(err);
        }
        originalConfig._retry = true;

        // Refreshing token only once
        const result = await memoizedRefreshToken();

        if (result) {
            originalConfig.headers = {
                ...originalConfig.headers,
                Authorization: `Bearer ${result.accessToken}`,
            };
            return instance(originalConfig);
        } else {
            return Promise.reject(err);
        }
    }
);

// Catch error during token refreshing process.
refreshTokenInstance.interceptors.response.use(
    res => {
        return res;
    },
    async err => {
        refreshTokenInstance.post('/auth/logout', { refreshToken: getToken(REFRESH_TOKEN) });
        removeToken(TOKEN);
        removeToken(REFRESH_TOKEN);

        await globalNotification.open({
            type: notificationType.ERROR,
            message: 'Your session has expired.',
            description: 'Please proceed to sign-in',
        });

        setTimeout(() => window.location.replace('/auth/sign-in'), 3000);
    }
);

function navigateWithError(status, subTitle) {
    globalRouter.navigate(routesPath.ERROR.PATH, {
        state: {
            status,
            title: status,
            subTitle,
        },
    });
}

export const axiosInstance = instance;

export const axiosRefTokenInst = refreshTokenInstance;

export const CancelSignal = axios.CancelToken.source;

export const isCancelSignal = e => axios.isCancel(e);

export const useAxios = () => {
    const signal = CancelSignal();

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            signal.cancel('Unmount useAxios');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { axios: axiosInstance, cancelToken: signal.token };
};
